import { Link, useLocation } from "react-router-dom";
import { useState, useRef, useEffect } from "react";
import ProfileDropdown from "./Profile";
import styles from "./header.module.css";
import Logo from "./Logo.js";
import { useGlobalContext } from "../../Context/GlobalContext";
import CustomModal from "../Modal/CustomModal";
import BorderedButton from "../Buttons/BorderedButton";
import PrimaryButton from "../Buttons/PrimaryButton";
import StarIcon from "../Icons/StarIcon.js";
import SearchIcon from "../Icons/SearchIcon";
import FilterIcon from "../Icons/FilterIcon";
import "../Popups/popup.common.css";
import { clearTempBookingId } from "../../Utils/tabHoldSession";

const VERSION_FILE_URL = "/version.json";
const DEPLOYMENT_VERSION_STORAGE_KEY = "natureland_deployment_version";
const DEPLOYMENT_BUILD_TIME_STORAGE_KEY = "natureland_deployment_build_time";
const SESSION_STORAGE_CACHE_KEYS_TO_CLEAR = [
	"lastBookingsFetchKey",
	"lastTherapistListFetchKey",
	"serviceList",
];

const Header = () => {
	const location = useLocation();
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
	const [updateAvailable, setUpdateAvailable] = useState(false);
	const [showUpdatePopup, setShowUpdatePopup] = useState(false);
	const [latestUpdateInfo, setLatestUpdateInfo] = useState({
		version: "",
		buildTime: "",
		changes: [],
	});
	const menuRef = useRef(null);
	const buttonRef = useRef(null);
	const latestDeployedVersionRef = useRef("");
	const latestDeployedBuildTimeRef = useRef("");
	const latestDeployedChangesRef = useRef([]);
	const currentDeploymentVersionRef = useRef(
		localStorage.getItem(DEPLOYMENT_VERSION_STORAGE_KEY) || ""
	);
	const currentDeploymentBuildTimeRef = useRef(
		localStorage.getItem(DEPLOYMENT_BUILD_TIME_STORAGE_KEY) || ""
	);
	const isHome = location.pathname === "/";

	useEffect(() => {
		const handleClickOutside = (event) => {
			if (
				isMenuOpen &&
				menuRef.current &&
				!menuRef.current.contains(event.target) &&
				buttonRef.current &&
				!buttonRef.current.contains(event.target)
			) {
				setIsMenuOpen(false);
			}
		};
		document.addEventListener("mousedown", handleClickOutside);
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isMenuOpen]);

	// useEffect(() => {
	// 	const checkForNewDeployment = async () => {
	// 		try {
	// 			const response = await fetch(
	// 				`${VERSION_FILE_URL}?t=${Date.now()}`,
	// 				{ cache: "no-store" }
	// 			);
	// 			if (!response.ok) return;

	// 			const data = await response.json();
	// 			const deployedVersion = data?.version || "";
	// 			const deployedBuildTime = data?.buildTime || "";
	// 			const deployedChanges = Array.isArray(data?.changes)
	// 				? data.changes.filter(Boolean)
	// 				: [];
	// 			if (!deployedVersion && !deployedBuildTime) return;

	// 			latestDeployedVersionRef.current = deployedVersion;
	// 			latestDeployedBuildTimeRef.current = deployedBuildTime;
	// 			latestDeployedChangesRef.current = deployedChanges;

	// 			if (
	// 				!currentDeploymentVersionRef.current && !currentDeploymentBuildTimeRef.current
	// 			) {
	// 				currentDeploymentVersionRef.current = deployedVersion;
	// 				currentDeploymentBuildTimeRef.current = deployedBuildTime;
	// 				localStorage.setItem(
	// 					DEPLOYMENT_VERSION_STORAGE_KEY,
	// 					deployedVersion
	// 				);
	// 				localStorage.setItem(
	// 					DEPLOYMENT_BUILD_TIME_STORAGE_KEY,
	// 					deployedBuildTime
	// 				);
	// 				return;
	// 			}

	// 			const versionChanged =!!deployedVersion && deployedVersion !== currentDeploymentVersionRef.current;
	// 			const buildTimeChanged =!!deployedBuildTime && deployedBuildTime !== currentDeploymentBuildTimeRef.current;
	// 			if (versionChanged || buildTimeChanged) {
	// 				setLatestUpdateInfo({
	// 					version: deployedVersion,
	// 					buildTime: deployedBuildTime,
	// 					changes: deployedChanges,
	// 				});
	// 				setUpdateAvailable(true);
	// 			}
	// 		} catch (error) {
	// 			// Ignore version check errors and keep normal app flow.
	// 		}
	// 	};

	// 	checkForNewDeployment();
	// 	const intervalId = window.setInterval(checkForNewDeployment, 120000);

	// 	return () => {
	// 		window.clearInterval(intervalId);
	// 	};
	// }, []);

	const handleUpdateClick = () => {
		setShowUpdatePopup(true);
	};

	const handleCancelUpdate = () => {
		setShowUpdatePopup(false);
	};

	const handleContinueUpdate = () => {
		const newVersionToStore =
			latestDeployedVersionRef.current || currentDeploymentVersionRef.current;
		const newBuildTimeToStore = latestDeployedBuildTimeRef.current || currentDeploymentBuildTimeRef.current;
		if (newVersionToStore) {
			localStorage.setItem(DEPLOYMENT_VERSION_STORAGE_KEY, newVersionToStore);
			currentDeploymentVersionRef.current = newVersionToStore;
		}
		if (newBuildTimeToStore) {
			localStorage.setItem(
				DEPLOYMENT_BUILD_TIME_STORAGE_KEY,
				newBuildTimeToStore
			);
			currentDeploymentBuildTimeRef.current = newBuildTimeToStore;
		}
		if (
			latestDeployedChangesRef.current &&
			latestDeployedChangesRef.current.length
		) {
			localStorage.setItem(
				"natureland_deployment_changes",
				JSON.stringify(latestDeployedChangesRef.current)
			);
		}

		clearTempBookingId();
		SESSION_STORAGE_CACHE_KEYS_TO_CLEAR.forEach((key) => {
			sessionStorage.removeItem(key);
		});

		window.location.reload();
	};

	const updateChanges =
		latestUpdateInfo.changes && latestUpdateInfo.changes.length
			? latestUpdateInfo.changes
			: [
					"Performance and stability improvements",
					"General bug fixes and minor enhancements",
			  ];
	const formattedBuildTime = latestUpdateInfo.buildTime
		? new Date(latestUpdateInfo.buildTime).toLocaleString()
		: "Not available";

	const {
		newAppointmentSlots,
		isCustomerSelected,
		selectedServices,
		setShowConflictModal,
		setIsSidebarVisible,
		setActiveSlotId,
		setIsBookingSidebar,
		setSelectedBookingId,
		setNewAppointmentSlots,
		setPaxes,
		setIsNewBookingSlotActive,
		setTriggerCancel,
		setEditPrefill,
		setServiceTotal,
		setAmountDue,
		setReference,
		setPaymentSummary,
		setPaymentHistory,	
		setCurrentPaymentMethod,
		setVoucherStates,
		setVoucherDetail,
		setSelectedServices,
		setCustomerData,
		setSelectedSource,
		setTemporaryBookingId,
	} = useGlobalContext();
	const isActive = (path) => location.pathname === path;
	const hasBookingConflict = () =>
		(newAppointmentSlots.length > 0 && isCustomerSelected) ||
		selectedServices.length > 0;
	const openHomeSearch = () => {
		if (!isHome) return;
		window.dispatchEvent(new CustomEvent("openHomeSearchPopup"));
	};
	const toggleHomeFilter = () => {
		if (!isHome) return;
		window.dispatchEvent(new CustomEvent("toggleHomeFilterPanel"));
	};
	const handleNavClick = (event) => {
		    setIsMenuOpen(false);
			// Close sidebar when navigating to another page (no conflict)
			setIsSidebarVisible(false);
			setSelectedBookingId(null);
			setActiveSlotId(null);
			setSelectedBookingId(null);
			setNewAppointmentSlots([]);
			try { setSelectedServices([]); } catch {}
			try { setPaxes([]); } catch {}
			try { setIsNewBookingSlotActive(false); } catch {}
			try { setTriggerCancel(true); } catch {}
			setEditPrefill(null);
			setServiceTotal(0);
			setAmountDue(0);
			setReference("");
			setPaymentSummary({
			  subtotal: 0,
			  midtotal: 0,
			  gst: 0,
			  tip: "0.00",
			  total: 0,
			  payment_method: "",
			});
			setPaymentHistory([]);
			setCurrentPaymentMethod("");
			setVoucherStates({});
			setVoucherDetail([]);
			setSelectedServices([]);
			setPaxes([]);
			setEditPrefill(null);
			setCustomerData({});
			setSelectedSource(null);
			setTemporaryBookingId(null);
			if(location.pathname !== "/") {
				sessionStorage.removeItem("skipCurrentTimeAutoScroll");
			}
	};

	return (
		<section className={`${styles.headerSection} ${isMenuOpen || isProfileDropdownOpen ? styles.menuOpen : ""}`}>
			<div className="container-fluid">
				<nav className={styles.navbar}>
					<div className={styles.sideHeader}>
						<div className={styles.navbarBrand}>
							<Link to="/">
								<Logo />
							</Link>
						</div>

						{/* Show the sidebar toggle only on the homepage */}
						{/* {location.pathname === "/" && (
							<div
								className={styles.sidebarToggle}
								onClick={toggleSidebar}>
								<img src={Icon} alt="Toggle Sidebar" />
							</div>
						)} */}
					</div>

					{isHome && (
						<div className={styles.mobileHomeActions}>
							<button
								type="button"
								className={styles.mobileSearch}
								onClick={openHomeSearch}
								aria-label="Search bookings">
								<SearchIcon size={16} />
								<span>Search bookings...</span>
							</button>
							<button
								type="button"
								className={styles.mobileFilter}
								onClick={toggleHomeFilter}
								aria-label="Filter bookings">
								<FilterIcon size={16} />
							</button>
						</div>
					)}

					<div className={styles.sideMenu}>
						{/* {updateAvailable && (
							<button
								type="button"
								className={styles.deploymentUpdateButton}
								title="New deployment available!"
								onClick={handleUpdateClick}
							>
								Update
							</button>
						)} */}
						<button
							ref={buttonRef}
							className={styles.navbarToggle}
							aria-label="Toggle navigation"
							onClick={() => setIsMenuOpen(!isMenuOpen)}>
							<p className={styles.navbarToggleIcon}>
								<span></span>
								<span></span>
								<span></span>
							</p>
						</button>

						<div
							ref={menuRef}
							id="responsive-navbar-nav"
							className={`${styles.navbarCollapse} ${
								isMenuOpen ? styles.show : ""
							}`}>
							<ul className={styles.navList}>
								<li className={styles.navItem}>
									<Link
										to="/"
										className={`${styles.navLink} ${
											isActive("/") ? styles.active : ""
										}`}
										onClick={handleNavClick}>
										Home
									</Link>
								</li>
								<li className={styles.navItem}>
									<Link
										to="/therapists"
										className={`${styles.navLink} ${
											isActive("/therapists")
												? styles.active
												: ""
										}`}
										onClick={handleNavClick}>
										Therapists
									</Link>
								</li>
								<li className={styles.navItem}>
									<Link
										to="/sales"
										className={`${styles.navLink} ${
											isActive("/sales")
												? styles.active
												: ""
										}`}
										onClick={handleNavClick}>
										Sales
									</Link>
								</li>
								<li className={styles.navItem}>
									<Link
										to="/clients"
										className={`${styles.navLink} ${
											isActive("/clients")
												? styles.active
												: ""
										}`}
										onClick={handleNavClick}>
										Clients
									</Link>
								</li>
								{/* <li className={styles.navItem}>
									<Link
										to="/transactions"
										className={`${styles.navLink} ${
											isActive("/transactions")
												? styles.active
												: ""
										}`}>
										Transactions
									</Link>
								</li> */}
								<li className={styles.navItem}>
									<Link
										to="/reports"
										className={`${styles.navLink} ${
											isActive("/reports")
												? styles.active
												: ""
										}`}
										onClick={handleNavClick}>
										Reports
									</Link>
								</li>
								<li className={styles.navItem}>
									<Link
										to="https://docs.google.com/document/d/1RLjIidV6jdooM7wff5ZYQ5LKyNlqgwHaHO3_ujmO6QY/edit?tab=t.0#heading=h.8bbwcg2h8myh"
										className={`${styles.navLink} ${
											isActive("/whats-new")
												? styles.active
												: ""
										}`}
										target="_blank"
										rel="noopener noreferrer"
										onClick={handleNavClick}>
											<span className={styles.whatsNewIcon}><StarIcon size={16} fill="#3c2212" starFill="#e7ad1b" /></span>What's New
									</Link>
								</li>
							</ul>
						</div>
						{/* <NotificationDropdown /> */}
						<ProfileDropdown onOpenChange={setIsProfileDropdownOpen} />
					</div>
				</nav>
			</div>
			<CustomModal
				show={showUpdatePopup}
				onHide={handleCancelUpdate}
				hideCloseButton={true}
			>
				<div className={`popup ${styles.updateConfirmPopup}`}>
					<div className="header">
						<h2>New Updates Available</h2>
						<p>
							<b>Version:</b> {latestUpdateInfo.version || "Not available"}
						</p>
					</div>
					<div className="body">
						<div className={styles.updatePopupChanges}>
							<h4>What&apos;s new</h4>
							<ul>
								{updateChanges.map((change, index) => (
									<li key={`${change}-${index}`}>{change}</li>
								))}
							</ul>
						</div>
					</div>
					<div className="footer">
						<BorderedButton text="Cancel" onClick={handleCancelUpdate} />
						<PrimaryButton text="Continue" onClick={handleContinueUpdate} />
					</div>
				</div>
			</CustomModal>
		</section>
	);
};

export default Header;
